import React, { useEffect, useMemo, useState } from "react";
import { toast, Toaster } from "sonner";
import DashboardShell from "../../components/layout/DashboardShell";
import { Pill, Button } from "../../components/common/Primitives";
import AdminListSearch from "../../components/common/AdminListSearch";
import OnboardModal from "../../components/common/OnboardModal";
import AddToDealRoomModal from "../../components/admin/AddToDealRoomModal";
import AdminApprovalBadge from "../../components/common/AdminApprovalBadge";
import ConfirmReasonDialog from "../../components/common/ConfirmReasonDialog";
import api, { formatApiError } from "../../lib/api";
import { useAuth } from "../../lib/AuthContext";
import { UserPlus, DoorOpen } from "lucide-react";

export default function AdminInvestors() {
  const { user } = useAuth();
  const [rows, setRows] = useState([]);
  const [team, setTeam] = useState([]);
  const [query, setQuery] = useState("");
  const [showOnboard, setShowOnboard] = useState(false);
  const [dealRoomFor, setDealRoomFor] = useState(null);
  const [forceApproveFor, setForceApproveFor] = useState(null); // investor row
  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return rows;
    return rows.filter((r) => {
      const hay = [r.investor_name, r.firm_name, r.owner_email, r.owner_user_id,
                   r.investor_type, r.city, r.status, r.kyc_status, r.aum]
        .filter(Boolean).join(" ").toLowerCase();
      return hay.includes(q);
    });
  }, [rows, query]);
  const load = async () => {
    try {
      const [r, t] = await Promise.all([api.get("/admin/investors"), api.get("/admin/team")]);
      setRows(r.data || []);
      setTeam(t.data || []);
    } catch (e) { toast.error(formatApiError(e)); }
  };
  useEffect(() => { load(); }, []);

  const setStatus = async (id, status) => {
    try {
      await api.post(`/admin/investors/${id}/status`, { status });
      toast.success(`Marked ${status}.`);
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const forceApprove = (row) => setForceApproveFor(row);
  const confirmForceApprove = async ({ reason }) => {
    const row = forceApproveFor;
    if (!row) return;
    try {
      await api.post(`/admin/investors/${row.investor_id}/force-approve`, { note: reason, override_kyc: true });
      toast.success("Force-approved.");
      setForceApproveFor(null);
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const assignManager = async (id, user_id) => {
    if (!user_id) return;
    try {
      await api.post(`/admin/investors/${id}/assign-manager`, { user_id });
      toast.success("Manager assigned.");
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <DashboardShell
      title="Investors"
      subtitle="ADMIN · APPROVALS"
      actions={
        !user?.demo_account && (
          <button
            onClick={() => setShowOnboard(true)}
            className="inline-flex items-center gap-1.5 px-3 py-2 rounded-md bg-emerald-700 text-white text-xs font-heading font-semibold hover:bg-emerald-800 transition-colors"
            data-testid="btn-onboard-investor"
          >
            <UserPlus size={13} /> Onboard investor
          </button>
        )
      }
    >
      <Toaster richColors position="top-right" />
      <OnboardModal
        open={showOnboard}
        onClose={() => setShowOnboard(false)}
        kind="investor"
        actorRole={user?.role}
        onDone={() => load()}
      />
      <AddToDealRoomModal
        open={!!dealRoomFor}
        onClose={() => setDealRoomFor(null)}
        mode="investor"
        pinned={dealRoomFor}
        onCreated={() => load()}
      />
      <ConfirmReasonDialog
        open={!!forceApproveFor}
        title={`Force-approve ${forceApproveFor?.investor_name || forceApproveFor?.firm_name || forceApproveFor?.owner_email || "this investor"}?`}
        description="SUPER-ADMIN OVERRIDE — flips both profile status AND KYC status to Approved instantly, bypassing document review and Sandbox verification. Logged with your email."
        confirmLabel="Force Approve"
        tone="danger"
        reasonPlaceholder="Reason (visible in audit log + investor email)"
        onConfirm={confirmForceApprove}
        onClose={() => setForceApproveFor(null)}
        testId="investors-force-approve-dialog"
      />
      <AdminListSearch
        value={query}
        onChange={setQuery}
        placeholder="Search by name, firm, email, type, city…"
        testId="admin-investors-search"
        count={filtered.length}
      />
      <div className="glass overflow-x-auto rounded-lg">
        <table className="w-full text-sm">
          <thead className="text-[10px] uppercase tracking-widest text-ink-muted">
            <tr className="border-b border-navy/10">
              <th className="text-left px-4 py-3">Investor</th>
              <th className="text-left px-4 py-3">Type</th>
              <th className="text-left px-4 py-3">City</th>
              <th className="text-left px-4 py-3">AUM</th>
              <th className="text-left px-4 py-3">Status</th>
              <th className="text-left px-4 py-3">Manager</th>
              <th className="text-left px-4 py-3">Actions</th>
            </tr>
          </thead>
          <tbody>
            {filtered.map((r) => (
              <tr key={r.investor_id} className="border-b border-navy/5" data-testid={`admin-investor-${r.investor_id}`}>
                <td className="px-4 py-3 text-ink font-heading">
                  <div>{r.investor_name || r.firm_name || r.owner_user_id}</div>
                  <div className="mt-1"><AdminApprovalBadge entity={r} size="sm" testId={`admin-approval-${r.investor_id}`} /></div>
                </td>
                <td className="px-4 py-3 text-ink-soft">{r.investor_type || "—"}</td>
                <td className="px-4 py-3 text-ink-soft">{r.city || "—"}</td>
                <td className="px-4 py-3 text-ink-soft">{r.aum || "—"}</td>
                <td className="px-4 py-3"><Pill tone={r.status === "Approved" ? "success" : r.status === "Rejected" ? "danger" : "warn"}>{r.status}</Pill></td>
                <td className="px-4 py-3">
                  <select
                    value={r.assigned_manager_user_id || ""}
                    onChange={(e) => assignManager(r.investor_id, e.target.value)}
                    className="text-xs bg-white border border-navy/15 rounded px-2 py-1 text-ink focus:border-gold outline-none"
                    data-testid={`inv-manager-select-${r.investor_id}`}
                  >
                    <option value="">— Unassigned —</option>
                    {team.map((t) => {
                      const label = `${t.name || t.email} · ${t.role}`;
                      return <option key={t.user_id} value={t.user_id}>{label}</option>;
                    })}
                  </select>
                </td>
                <td className="px-4 py-3 space-x-2 whitespace-nowrap">
                  <Button variant="dark" onClick={() => setStatus(r.investor_id, "Approved")} data-testid={`approve-inv-${r.investor_id}`}>Approve</Button>
                  <Button variant="dark" onClick={() => setStatus(r.investor_id, "Rejected")} data-testid={`reject-inv-${r.investor_id}`}>Reject</Button>
                  <Button
                    variant="dark"
                    onClick={() => setDealRoomFor(r)}
                    className="!bg-emerald-700 hover:!bg-emerald-800"
                    data-testid={`open-deal-room-inv-${r.investor_id}`}
                    title="Open a deal room between this investor and any startup"
                  >
                    <DoorOpen size={12} className="inline mr-1" />Deal Room
                  </Button>
                  {user?.role === "super_admin" && !user?.demo_account && r.status !== "Approved" && (
                    <Button
                      variant="dark"
                      onClick={() => forceApprove(r)}
                      className="!bg-amber-600 hover:!bg-amber-700"
                      data-testid={`force-approve-inv-${r.investor_id}`}
                      title="Super-admin override: skip KYC + docs review"
                    >
                      ⚡ Force Approve
                    </Button>
                  )}
                </td>
              </tr>
            ))}
            {filtered.length === 0 && (
              <tr><td colSpan={7} className="text-center py-10 text-ink-faint">
                {rows.length === 0 ? "No investors yet." : "No investors match your search."}
              </td></tr>
            )}
          </tbody>
        </table>
      </div>
    </DashboardShell>
  );
}
